import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import useCurrentPage from '../../../../hooks/useCurrentPage';
import Button from '../../../../../modules/react/components/stateless/Button';
import { TRACK_CLICK_RESERVE } from '../../../../../src/web/utils/dataLayer';
import RentalModal from '../../containers/RentalModal';
import Call from '../../../../components/stateful/UnitsTable/columns/Actions/actions/Call';

import styles from './RentalButton.scss';

const propTypes = {
  closeSelectedUnitModal: PropTypes.func,
  callText: PropTypes.string.isRequired,
  facility: PropTypes.shape({
    id: PropTypes.string.isRequired,
    reservationFeeRequired: PropTypes.bool,
  }).isRequired,
  inModalContext: PropTypes.bool,
  showCallButton: PropTypes.bool,
  moveInText: PropTypes.string,
  reserveText: PropTypes.string,
  reserveUrl: PropTypes.string,
  showRentalShortFormModal: PropTypes.bool,
  startText: PropTypes.string,
  toggleRentalShortFormModal: PropTypes.func.isRequired,
  unitGroupId: PropTypes.string.isRequired,
  inTierCard: PropTypes.bool,
  setTrackAction: PropTypes.func,
};

const defaultProps = {
  showCallButton: false,
  closeSelectedUnitModal: () => null,
  inModalContext: false,
  moveInText: 'Move In',
  reserveText: 'Reserve',
  reserveUrl: undefined,
  showRentalShortFormModal: false,
  startText: 'Get Started',
  inTierCard: false,
  setTrackAction: undefined,
};

const RentalButton = ({
  showCallButton,
  callText,
  closeSelectedUnitModal,
  facility,
  inModalContext,
  moveInText,
  reserveText,
  reserveUrl,
  showRentalShortFormModal,
  startText,
  toggleRentalShortFormModal,
  unitGroupId,
  tier,
  inTierCard,
  isUnitGroupExpanded,
  ...rest
}) => {
  const { setTrackAction } = rest;
  const toggleModal = () => {
    if (setTrackAction) {
      setTrackAction('shortFormRentalButton');
    }
    if (inModalContext) {
      closeSelectedUnitModal();
    }
    toggleRentalShortFormModal();
  };

  const {
    primaryColorBackgroundHoverClass,
    secondaryColorBackgroundClass,
    buttonStyleClass,
  } = useCurrentPage();

  const buttonClass = cx(
    secondaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    buttonStyleClass,
    'action-reservation', // see css in each UnitsTable theme for visibility rules
  );

  // link out to SERC if the facility requires a fee
  const { reservationFeeRequired = false } = facility;

  if (reservationFeeRequired) {
    return (
      <Button
        block
        className={cx(buttonClass, {
          [styles.rentalButton]: !isUnitGroupExpanded,
          [styles.rentalButtonInTiers]: isUnitGroupExpanded,
        })}
        href={reserveUrl}
        rel="noopener noreferrer"
        target="_blank"
        {...TRACK_CLICK_RESERVE}
      >
        {reserveText}
      </Button>
    );
  }

  // We are not proud of this, but wrapping the button in a <div> prevents the
  // .rnl-Modal-Footer styling from adding a margin to the "Select Different Unit"
  // button on mobile

  const shouldShowRentalModal = tier
    ? showRentalShortFormModal && inModalContext
    : showRentalShortFormModal;


  return (

    <>
      {showCallButton ? (
        <Call facility={facility} text={callText} unitGroupId={unitGroupId} {...rest} />
      ) : (
        <>
          {!inTierCard && (
            <Button
              block
              className={cx(buttonClass, {
                [styles.rentalButton]: !isUnitGroupExpanded,
                [styles.rentalButtonInTiers]: isUnitGroupExpanded,
              })}
              onClick={toggleModal}
            >
              {startText}
            </Button>
          )}
          {shouldShowRentalModal && (
            <RentalModal
              facilityId={facility.id}
              moveInText={moveInText}
              onClose={toggleModal}
              reserveText={reserveText}
              startText={startText}
              unitGroupId={unitGroupId}
              facility={facility}
              tier={tier}
              {...rest}
            />
          )}
        </>
      )}
    </>
  );
};

RentalButton.propTypes = propTypes;
RentalButton.defaultProps = defaultProps;

export default RentalButton;
